import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import useLocaleText from 'components/useLocaleText'
import { BaseModalV3, Button, CloseButton } from 'components/ui'
import styles from './FlowSelectDialog.module.css'

type Flow = 'flat' | 'grouped'

interface IFlowSelectDialogProps {
  isOpen: boolean
  onClose: () => void
  onSelectFlowA: () => void
  onSelectFlowB: () => void
  isLoading?: boolean
}

const literals = {
  title: {
    key: 'task_creation_flow_title',
    fallback: { en: 'How should the subtasks be organised?', he: 'כיצד לארגן את תת־המשימות?' },
  },
  subtitle: {
    key: 'task_creation_flow_subtitle',
    fallback: {
      en: 'You can change this later while building the task.',
      he: 'אפשר לשנות זאת מאוחר יותר במהלך בניית המשימה.',
    },
  },
  flat: { key: 'list_view', fallback: { en: 'Flat list', he: 'רשימה שטוחה' } },
  grouped: { key: 'cat_view', fallback: { en: 'Grouped by category', he: 'מקובץ לפי קטגוריה' } },
  flatDescription: {
    key: 'task_creation_flat_flow_description',
    fallback: {
      en: 'Subtasks run in one order. Best for a short checklist the executor works top to bottom.',
      he: 'תת־המשימות מופיעות ברצף אחד. מתאים לרשימה קצרה שהמבצע עובר עליה מלמעלה למטה.',
    },
  },
  groupedDescription: {
    key: 'task_creation_grouped_flow_description',
    fallback: {
      en: 'Subtasks sit under category headers. Best for long audits split by area or theme.',
      he: 'תת־המשימות מופיעות תחת כותרות קטגוריה. מתאים לביקורות ארוכות המחולקות לפי אזור או נושא.',
    },
  },
  recommendation: {
    key: 'task_creation_flat_flow_recommendation',
    fallback: { en: 'Flat list suits most instant tasks', he: 'רשימה שטוחה מתאימה לרוב המשימות המיידיות' },
  },
  cancel: { key: 'cancel', fallback: { en: 'Cancel', he: 'ביטול' } },
  continue: { key: 'continue', fallback: { en: 'Continue', he: 'המשך' } },
}

const FlatPreview = () => (
  <span className={styles.preview} aria-hidden="true">
    <i />
    <i />
    <i />
    <i />
  </span>
)

const GroupedPreview = () => (
  <span className={`${styles.preview} ${styles.groupedPreview}`} aria-hidden="true">
    <b />
    <i />
    <i />
    <b />
    <i />
  </span>
)

function FlowOption({
  flow,
  selected,
  label,
  description,
  disabled,
  onSelect,
}: {
  flow: Flow
  selected: boolean
  label: string
  description: string
  disabled: boolean
  onSelect: (flow: Flow) => void
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={`${styles.option}${selected ? ` ${styles.selected}` : ''}`}
      disabled={disabled}
      onClick={() => onSelect(flow)}
    >
      <span className={styles.optionHeading}>
        <strong>{label}</strong>
        <span className={styles.radio} aria-hidden="true" />
      </span>
      {flow === 'flat' ? <FlatPreview /> : <GroupedPreview />}
      <span className={styles.description}>{description}</span>
    </button>
  )
}

export const FlowSelectDialog: React.FC<IFlowSelectDialogProps> = ({
  isOpen,
  onClose,
  onSelectFlowA,
  onSelectFlowB,
  isLoading = false,
}) => {
  const [selected, setSelected] = useState<Flow>('flat')
  const locale = useSelector((state: RootState) => state.general.locale)
  const t = useLocaleText(literals)

  useEffect(() => {
    if (isOpen) setSelected('flat')
  }, [isOpen])

  const continueWithSelection = () => {
    if (selected === 'flat') onSelectFlowA()
    else onSelectFlowB()
  }

  return (
    <BaseModalV3
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
      trapFocus
      overlayProps={{ className: styles.overlay }}
      contentProps={{
        className: styles.dialog,
        'aria-labelledby': 'vm-flow-dialog-title',
        'aria-describedby': 'vm-flow-dialog-description',
        dir: locale === 'he' ? 'rtl' : 'ltr',
        style: { width: 'min(690px, calc(100vw - 32px))', minWidth: 0, maxWidth: 690, borderRadius: 16 },
      }}
      headerProps={{ className: styles.header, style: { padding: '22px 24px 14px', boxShadow: 'none' } }}
      bodyProps={{
        className: styles.body,
        style: {
          padding: '4px 24px 18px',
          height: 'auto',
          maxHeight: 'none',
          minHeight: 0,
          background: '#fff',
          overflow: 'visible',
        },
      }}
      footerProps={{
        className: styles.footer,
        style: { padding: '0 24px 24px', background: '#fff', justifyContent: 'space-between' },
      }}
      header={
        <div className={styles.titleRow}>
          <div>
            <h2 id="vm-flow-dialog-title">{t.title}</h2>
            <p id="vm-flow-dialog-description">{t.subtitle}</p>
          </div>
          <CloseButton onClick={onClose} isDisabled={isLoading} />
        </div>
      }
      body={
        <div className={styles.options} role="radiogroup" aria-label={t.title} aria-busy={isLoading}>
          <FlowOption
            flow="flat"
            selected={selected === 'flat'}
            label={t.flat}
            description={t.flatDescription}
            disabled={isLoading}
            onSelect={setSelected}
          />
          <FlowOption
            flow="grouped"
            selected={selected === 'grouped'}
            label={t.grouped}
            description={t.groupedDescription}
            disabled={isLoading}
            onSelect={setSelected}
          />
        </div>
      }
      footer={
        <>
          <p className={styles.recommendation}>{t.recommendation}</p>
          <div className={styles.actions}>
            <Button variant="secondary" onClick={onClose} isDisabled={isLoading}>
              {t.cancel}
            </Button>
            <Button
              variant="primary"
              onClick={continueWithSelection}
              isLoading={isLoading}
              isDisabled={isLoading}
              bg="#22272a"
              _hover={{ bg: '#22272a' }}
              _active={{ bg: '#171a1c' }}
            >
              {t.continue}
            </Button>
          </div>
        </>
      }
    />
  )
}
